import {ChangeDetectionStrategy, Component, computed, input, output, signal} from '@angular/core';
import {FormControl} from '@angular/forms';
import {TranslatePipe} from '@brunata-angular/util';
import {ExportNoteComponent} from '../../shared/ui/export-note/export-note.component';
import {DismountedMetersSectionComponent} from './dismounted-meters-section.component';
import {MeterScope} from '../meter-scope/meter-scope.model';
import {VendorModelsPanelComponent} from '../meter-scope/vendor-models-panel.component';
import {ChannelsListComponent} from '../meter-scope/channels-list.component';
import {StepCardDirective} from '../step-card/step-card.directive';
import {stepLabels} from '../step-card/step-labels.model';

export type MeterTypeCard = {
    meterSuperTypeNo: string;
    nameKey: string;
    selected: boolean;
};

@Component({
    selector: 'app-meter-types-step',
    standalone: true,
    providers: [
        stepLabels(
            'customizedexports_export_step_supertypes_title',
            'customizedexports_export_step_supertypes_hint',
        ),
    ],
    hostDirectives: [{directive: StepCardDirective, inputs: ['titleKey', 'hintKey']}],
    imports: [
        TranslatePipe,
        ExportNoteComponent,
        DismountedMetersSectionComponent,
        VendorModelsPanelComponent,
        ChannelsListComponent,
    ],
    template: `
        <app-dismounted-meters-section [control]="includeDismountedControl()" />
        @if (meterTypes() === undefined) {
            <p class="text-muted small mb-0">
                {{
                    'customizedexports_export_step_supertypes_loading'
                        | translate: 'online' : 'export'
                }}
            </p>
        } @else if (meterTypes() === null) {
            <app-export-note icon="bi-exclamation-triangle" tone="muted">
                {{
                    'customizedexports_export_step_supertypes_load_error'
                        | translate: 'online' : 'export'
                }}
            </app-export-note>
        } @else if (meterTypes(); as available) {
            @if (available.length === 0) {
                <app-export-note icon="bi-info-circle-fill">
                    {{
                        'customizedexports_export_step_supertypes_empty'
                            | translate: 'online' : 'export'
                    }}
                </app-export-note>
            } @else {
                <div class="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-2">
                    <span class="small text-muted">
                        {{
                            'customizedexports_export_step_supertypes_selected_count'
                                | translate
                                    : 'online'
                                    : 'export'
                                    : [selectedCount().toString(), available.length.toString()]
                        }}
                    </span>
                    <div class="d-flex gap-2">
                        <button
                            type="button"
                            class="btn btn-sm btn-outline-primary"
                            (click)="allSelected.emit()">
                            {{
                                'customizedexports_export_step_supertypes_select_all'
                                    | translate: 'online' : 'export'
                            }}
                        </button>
                        <button
                            type="button"
                            class="btn btn-sm btn-outline-secondary"
                            (click)="cleared.emit()">
                            {{
                                'customizedexports_export_step_supertypes_clear_all'
                                    | translate: 'online' : 'export'
                            }}
                        </button>
                    </div>
                </div>
                <div
                    class="row row-cols-2 row-cols-sm-3 row-cols-xl-4 g-2"
                    role="group"
                    [attr.aria-label]="
                        'customizedexports_export_step_supertypes_title'
                            | translate: 'online' : 'export'
                    ">
                    @for (meterType of available; track meterType.meterSuperTypeNo) {
                        <div class="col">
                            <label
                                class="border p-2 d-flex align-items-center gap-2 h-100 user-select-none mb-0 type-tile"
                                [class.border-primary]="meterType.selected"
                                [class.bg-primary-faint]="meterType.selected">
                                <input
                                    type="checkbox"
                                    class="form-check-input m-0 flex-shrink-0"
                                    [checked]="meterType.selected"
                                    (change)="meterTypeToggled.emit(meterType.meterSuperTypeNo)" />
                                <span class="small fw-bold text-truncate">
                                    {{ meterType.nameKey | translate: 'online' : 'common' }}
                                </span>
                            </label>
                        </div>
                    }
                </div>

                <div class="border mt-4">
                    <app-channels-list
                        class="d-block"
                        [channels]="meterScope().channels"
                        [superTypes]="meterScope().superTypes"
                        [loading]="meterScope().loading"
                        [failed]="meterScope().failed"
                        [offersNoChannels]="meterScope().offersNoChannels"
                        [noSuperTypesSelected]="selectedCount() === 0"
                        [(expanded)]="channelsExpanded"
                        (channelToggled)="channelToggled.emit($event)"
                        (channelsSelected)="channelsSelected.emit($event)"
                        (channelsDeselected)="channelsDeselected.emit($event)" />
                    @if (showsMeterScope()) {
                        <app-vendor-models-panel
                            class="d-block border-top"
                            [models]="meterScope().models"
                            [vendorNames]="meterScope().vendorNames"
                            [superTypes]="meterScope().superTypes"
                            [(expanded)]="modelsExpanded"
                            (modelToggled)="modelToggled.emit($event)"
                            (modelsSelected)="modelsSelected.emit($event)"
                            (modelsDeselected)="modelsDeselected.emit($event)" />
                    }
                    @if (meterScope().channelLess.length > 0) {
                        @let channelLessLabel =
                            'customizedexports_export_channels_without_channel'
                                | translate: 'online' : 'export';
                        <div
                            class="border-top d-flex align-items-center flex-wrap gap-3 px-3 py-2"
                            role="group"
                            [attr.aria-label]="channelLessLabel">
                            <span class="fw-bold">{{ channelLessLabel }}</span>
                            @for (row of meterScope().channelLess; track row.meterSuperTypeNo) {
                                <label
                                    class="d-flex align-items-center gap-2 mb-0 small user-select-none type-tile">
                                    <input
                                        type="checkbox"
                                        class="form-check-input m-0"
                                        [checked]="row.selected"
                                        (change)="channelLessToggled.emit(row.meterSuperTypeNo)" />
                                    {{ row.nameKey | translate: 'online' : 'common' }}
                                </label>
                            }
                        </div>
                    }
                </div>
            }
        }
    `,
    styles: `
        .type-tile {
            cursor: pointer;
        }
    `,
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MeterTypesStepComponent {
    readonly includeDismountedControl = input.required<FormControl<boolean>>();
    readonly meterTypes = input.required<ReadonlyArray<MeterTypeCard> | null | undefined>();
    readonly meterScope = input.required<MeterScope>();

    readonly meterTypeToggled = output<string>();
    readonly allSelected = output<void>();
    readonly cleared = output<void>();
    readonly modelToggled = output<string>();
    readonly modelsSelected = output<string[]>();
    readonly modelsDeselected = output<string[]>();
    readonly channelToggled = output<string>();
    readonly channelsSelected = output<string[]>();
    readonly channelsDeselected = output<string[]>();
    readonly channelLessToggled = output<string>();

    protected readonly selectedCount = computed(
        () => (this.meterTypes() ?? []).filter(meterType => meterType.selected).length,
    );

    protected readonly showsMeterScope = computed(() => this.meterScope().models.length > 0);

    protected readonly modelsExpanded = signal(true);

    protected readonly channelsExpanded = signal(true);
}
